import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

/**
 * Copy text to the clipboard with a fallback that works on insecure origins.
 *
 * `navigator.clipboard` is only available in secure contexts (HTTPS / localhost).
 * Over plain HTTP (e.g. a Tailscale IP) it is undefined, so we fall back to
 * selecting an element and document.execCommand('copy').
 *
 * IMPORTANT: when copying from inside a focus-trapping container (e.g. a MUI
 * Dialog), a detached/off-screen textarea appended to document.body loses its
 * selection because the trap steals focus back — execCommand then reports
 * success while copying nothing. Pass `selectEl` (an input/textarea that lives
 * INSIDE the dialog) so the selection holds.
 */
async function copyText(text, selectEl) {
   // Secure-context Clipboard API first.
   try {
      if (navigator.clipboard && window.isSecureContext) {
         await navigator.clipboard.writeText(text);
         return true;
      }
   } catch (e) {
      // fall through to the legacy path
   }

   let temp = null;
   try {
      let el = selectEl;
      if (!el) {
         temp = document.createElement('textarea');
         temp.value = text;
         temp.setAttribute('readonly', '');
         temp.style.position = 'fixed';
         temp.style.top = '-9999px';
         document.body.appendChild(temp);
         el = temp;
      }
      el.focus({ preventScroll: true });
      el.select();
      if (el.setSelectionRange) {
         try {
            el.setSelectionRange(0, text.length);
         } catch (e) {
            /* some inputs disallow setSelectionRange */
         }
      }
      const ok = document.execCommand('copy');
      return ok;
   } catch (e) {
      return false;
   } finally {
      if (temp) document.body.removeChild(temp);
   }
}

export default function useClipboard() {
   const { enqueueSnackbar } = useSnackbar();

   return useCallback(
      async (text, successMessage = 'Copied to clipboard', selectEl = null) => {
         const ok = await copyText(text, selectEl);
         enqueueSnackbar(
            ok ? successMessage : 'Could not copy — select the text and copy manually',
            { variant: ok ? 'success' : 'error' },
         );
         return ok;
      },
      [enqueueSnackbar],
   );
}
